<template>
  <v-card outlined>
    <v-card-text class="pa-0">
      <div class="delivery-tree-header px-4 py-3 font-weight-bold">
        <v-row>
          <v-col cols="12" md="3">Name</v-col>
          <v-col cols="12" md="2">Ref.</v-col>
          <v-col cols="12" md="1">Unit</v-col>
          <v-col cols="12" md="2">Quantity</v-col>
          <v-col cols="12" md="1">Number of Piece(s)</v-col>
          <v-col cols="12" md="1">Unit weight</v-col>
          <v-col cols="12" md="1">Total weight</v-col>
          <v-col cols="12" md="1"></v-col>
        </v-row>
      </div>

      <div
        v-for="(parent, parentIndex) in items"
        :key="parent.local_key"
        class="delivery-parent-row"
      >
        <div class="px-4 py-3 grey lighten-5">
          <v-row align="center">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="parent.name"
                outlined
                hide-details
                :readonly="!editable"
              />
            </v-col>
          
            <v-col cols="12" md="2">
              <v-text-field
                v-model="parent.reference"
                outlined
                hide-details
                :readonly="!editable"
              />
            </v-col>
          
            <v-col cols="12" md="1">
              <v-text-field
                v-model="parent.unit"
                outlined
                hide-details
                :readonly="!editable"
              />
            </v-col>
          
            <v-col cols="12" md="2">
              <v-text-field
                v-model.number="parent.quantity"
                type="number"
                min="0"
                step="0.0001"
                outlined
                hide-details
                :readonly="!editable"
                @input="emitRecalculate"
              />
            </v-col>
          
            <v-col cols="12" md="1">
              <v-text-field
                v-model.number="parent.num_of_pcs"
                type="number"
                min="0"
                step="0.0001"
                outlined
                hide-details
                :readonly="!editable"
                @input="emitRecalculate"
              />
            </v-col>
          
            <v-col cols="12" md="1">
              <v-text-field
                v-model.number="parent.weight_per_unit"
                type="number"
                min="0"
                step="0.0001"
                outlined
                hide-details
                :readonly="!editable"
                @input="emitRecalculate"
              />
            </v-col>
          
            <v-col cols="12" md="1" class="font-weight-bold">
              {{ rowWeight(parent) }}
            </v-col>
          
            <v-col cols="12" md="1" class="text-right">
              <v-menu offset-y v-if="editable">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
              
                <v-list>
                  <v-list-item @click="$emit('add-component', parentIndex)">
                    <v-list-item-title>Add component</v-list-item-title>
                  </v-list-item>
                
                  <v-list-item @click="$emit('remove-parent', parentIndex)">
                    <v-list-item-title class="red--text">Remove row</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="12">
              <v-text-field
                v-model="parent.comment"
                label="Comment"
                outlined
                hide-details
                :readonly="!editable"
              />
            </v-col>
          </v-row>
        </div>

        <div
          v-for="(child, childIndex) in parent.children"
          :key="child.local_key"
          class="px-4 py-3 blue lighten-5 child-row"
        >
          <v-row align="center">
            <v-col cols="12" md="3">
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-subdirectory-arrow-right</v-icon>
                <v-text-field
                  v-model="child.name"
                  outlined
                  hide-details
                  :readonly="!editable"
                />
              </div>
            </v-col>
          
            <v-col cols="12" md="2">
              <v-text-field
                v-model="child.reference"
                outlined
                hide-details
                :readonly="!editable"
              />
            </v-col>
          
            <v-col cols="12" md="1">
              <v-text-field
                v-model="child.unit"
                outlined
                hide-details
                :readonly="!editable"
              />
            </v-col>
          
            <v-col cols="12" md="2">
              <v-text-field
                v-model.number="child.quantity"
                type="number"
                min="0"
                step="0.0001"
                outlined
                hide-details
                :readonly="!editable"
                @input="markChildQuantityAsManual(child)"
              />
            </v-col>
          
            <v-col cols="12" md="1">
              <v-text-field
                v-model.number="child.num_of_pcs"
                type="number"
                min="0"
                step="0.0001"
                outlined
                hide-details
                :readonly="!editable"
                @input="markChildNumOfPcsAsManual(child)"
              />
            </v-col>
          
            <v-col cols="12" md="1">
              <v-text-field
                v-model.number="child.weight_per_unit"
                type="number"
                min="0"
                step="0.0001"
                outlined
                hide-details
                :readonly="!editable"
                @input="emitRecalculate"
              />
            </v-col>
          
            <v-col cols="12" md="1" class="font-weight-bold">
              {{ rowWeight(child) }}
            </v-col>
          
            <v-col cols="12" md="1" class="text-right">
              <v-btn
                v-if="editable"
                icon
                color="error"
                @click="$emit('remove-child', { parentIndex, childIndex })"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="child.comment"
                label="Comment"
                outlined
                hide-details
                :readonly="!editable"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                :value="child.is_auto_component ? 'Automatic component' : 'Manual component'"
                label="Type"
                outlined
                hide-details
                readonly
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-text-field
                :value="child.component_relation_quantity"
                label="Relational qty"
                outlined
                hide-details
                readonly
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-text-field
                :value="child.component_relation_num_of_pcs"
                label="Relational db"
                outlined
                hide-details
                readonly
              />
            </v-col>
          </v-row>
        </div>

        <v-divider />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "DeliveryNoteItemsTree",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    rowWeight(row) {
      const qty = Number(row.quantity || 0);
      const pcs = Number(row.num_of_pcs || 0);
      const weight = Number(row.weight_per_unit || 0);
      return (qty * pcs * weight).toFixed(2);
    },
    emitRecalculate() {
      this.$emit("recalculate");
    },
    
    markChildQuantityAsManual(child) {
      child.component_relation_quantity = null;
      this.emitRecalculate();
    },

    markChildNumOfPcsAsManual(child) {
      child.component_relation_num_of_pcs = null;
      this.emitRecalculate();
    },
  },
};
</script>

<style scoped>
.child-row {
  border-left: 4px solid #90caf9;
}
</style>